import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, MapPin, Package, Edit, Trash2, Building2 } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { renderCell, coalesce, cellText } from '../../utils/helpers';

const ClientSites = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [sites, setSites] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
  });

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientData, sitesData] = await Promise.all([
        apiService.getClient(clientId),
        apiService.getSitesByClient(clientId)
      ]);
      setClient(clientData);
      setSites(sitesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSite(null);
    setFormData({ name: '', address: '' });
  };

  const handleAdd = () => {
    setEditingSite(null);
    setFormData({ name: '', address: '' });
    setShowModal(true);
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name || '',
      address: site.address || '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Le nom du site est requis');
      return;
    }

    try {
      if (editingSite) {
        await apiService.updateSite(editingSite.id, formData);
        toast.success('Site modifié avec succès');
      } else {
        await apiService.createSite({
          ...formData,
          clientId: clientId
        });
        toast.success('Site créé avec succès');
      }
      closeModal();
      loadData();
    } catch (error) {
      console.error('Failed to save site:', error);
      toast.error('Erreur lors de la sauvegarde du site');
    }
  };

  const handleDelete = async (siteId) => {
    const site = sites.find((s) => s.id === siteId);
    const equipCount = site?.equipmentCount ?? site?.equipment_count ?? 0;
    const confirmMessage = equipCount > 0
      ? `Ce site contient ${equipCount} équipement(s).\n\nSupprimer le site ?`
      : 'Êtes-vous sûr de vouloir supprimer ce site ? Cette action est irréversible.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await apiService.deleteSite(siteId, equipCount > 0);
      toast.success('Site supprimé avec succès');
      loadData();
    } catch (error) {
      console.error('Failed to delete site:', error);
      if (error.hasEquipment) {
        toast.error(`Impossible de supprimer : ${error.equipmentCount || 0} équipement(s) sur ce site`);
      } else {
        toast.error(error.message || 'Erreur lors de la suppression du site');
      }
    }
  };

  const handleViewEquipment = (siteId) => {
    navigate(`/admin/sites/${siteId}/equipment`);
  };

  const getEquipmentCount = (site) => site?.equipmentCount ?? site?.equipment_count ?? 0;

  const columns = [
    {
      header: 'Nom du Site',
      accessor: 'name',
      render: (row) => (
        <div 
          className="flex items-center cursor-pointer group"
          onClick={() => handleViewEquipment(row.id)}
        >
          <MapPin className="w-4 h-4 text-red-500 mr-2 group-hover:text-red-600" />
          <span className="font-medium text-gray-900 group-hover:text-red-600 transition-colors">
            {renderCell(row.name)}
          </span>
        </div>
      ),
    },
    {
      header: 'Adresse',
      accessor: 'address',
      render: (row) => (
        <span className="text-gray-600">{renderCell(row.address)}</span>
      ),
    },
    {
      header: 'Nombre d\'équipements',
      accessor: 'equipmentCount',
      render: (row) => (
        <div className="flex items-center">
          <Package className="w-4 h-4 text-gray-400 mr-1" />
          <span className="font-medium text-gray-900">{getEquipmentCount(row)}</span>
        </div>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={Package}
            onClick={() => handleViewEquipment(row.id)}
            title="Voir les équipements de ce site"
          >
            Équipements
          </Button>
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Modifier le site"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Supprimer le site"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  const clientLabel = coalesce(client?.company, client?.name);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate('/clients')}
          className="mb-4"
        >
          Retour aux clients
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Sites - {cellText(clientLabel)}
            </h1>
            <p className="text-gray-600 mt-1">
              Gérez les sites et emplacements de ce client
            </p>
          </div>
          <Button icon={Plus} onClick={handleAdd}>
            Ajouter un site
          </Button>
        </div>
      </div>

      {/* Sites Table */}
      {sites.length > 0 ? (
        <Card>
          <Table data={sites} columns={columns} />
        </Card>
      ) : (
        <Card>
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun site trouvé pour ce client
            </h3>
            <p className="text-gray-600">
              Commencez par ajouter un site pour ce client
            </p>
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingSite ? 'Modifier le site' : 'Nouveau site'}
        size="sm"
      >
        <form onSubmit={handleSave} className="space-y-4">
          <Input
            label="Nom du site"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: Direction, Entrepôt, Cuisine..."
            required
          />

          <Textarea
            label="Adresse"
            name="address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            rows={3}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button type="submit">
              {editingSite ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClientSites;
